import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Button, Field, Input } from '@/shared/ui'
import { ApiError } from '@/shared/api/client'
import { guessTimezone } from '@/shared/config/timezones'
import { useRegister } from '../model'
import styles from './AuthForm.module.css'

/** Столько же требует сервер: расхождение даст ошибку уже после отправки. */
const MIN_PASSWORD = 8

/**
 * Регистрация в один шаг.
 *
 * Три поля вместо пяти. Часовой пояс не спрашиваем: браузер знает его
 * сам, а если ошибётся — настройщик переспросит в разговоре, где это
 * будет уместным вопросом, а не строкой в форме перед входом.
 */
export function RegisterForm() {
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [touchedPassword, setTouchedPassword] = useState(false)

  const register = useRegister()
  const error = register.error instanceof ApiError ? register.error : undefined

  // Пароль проверяем и на клиенте — не вместо сервера, а чтобы человек
  // узнал о коротком пароле сразу, а не после ожидания ответа.
  const passwordTooShort = touchedPassword && password.length > 0 && password.length < MIN_PASSWORD

  function submit(event: FormEvent) {
    event.preventDefault()
    if (password.length < MIN_PASSWORD) {
      setTouchedPassword(true)
      return
    }
    register.mutate({
      company: company.trim(),
      name: '',
      email: email.trim(),
      password,
      timezone: guessTimezone(),
    })
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      {error && !error.field && <div className={styles.error}>{error.message}</div>}

      <Field
        label="Название компании"
        required
        error={error?.field === 'company' ? error.message : undefined}
      >
        {(props) => (
          <Input
            {...props}
            placeholder="Синта"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            invalid={error?.field === 'company'}
            autoFocus
            required
          />
        )}
      </Field>

      <Field
        label="Рабочая почта"
        required
        error={error?.field === 'email' ? error.message : undefined}
      >
        {(props) => (
          <Input
            {...props}
            type="email"
            autoComplete="username"
            inputMode="email"
            placeholder="ivan@company.ru"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={error?.field === 'email'}
            required
          />
        )}
      </Field>

      <Field
        label="Пароль"
        required
        error={
          passwordTooShort
            ? `Не короче ${MIN_PASSWORD} символов`
            : error?.field === 'password'
              ? error.message
              : undefined
        }
      >
        {(props) => (
          <Input
            {...props}
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouchedPassword(true)}
            invalid={passwordTooShort || error?.field === 'password'}
            required
          />
        )}
      </Field>

      <span
        className={`${styles.requirement} ${
          password.length >= MIN_PASSWORD ? styles.requirementMet : ''
        }`}
      >
        <span className={styles.requirementDot} />
        не короче {MIN_PASSWORD} символов
      </span>

      <div className={styles.footer}>
        <Button type="submit" fullWidth loading={register.isPending}>
          Создать компанию
        </Button>
        <p className={styles.switcher}>
          Уже есть аккаунт?{' '}
          <Link className={styles.link} to="/login">
            Войти
          </Link>
        </p>
      </div>
    </form>
  )
}
